import {v4 as uuid} from 'uuid';
import isEmpty from 'lodash/isEmpty';
import {mxResources} from './mxGraphFactory';

const choiceTaskPrefix = 'swf::task::decision::';
const parallelTaskPrefix = 'swf::task::parallel::';

const taskWidth = 190;
const taskNameMaxLength = 80;
const documentReaderFields = {
    JsonDocumentReader: ['keysToUse', 'pointer'],
    TextDocumentReader: ['charset'],
    HtmlDocumentReader: ['charset', 'selector', 'separator', 'allElements', 'groupByElement', 'includeLinkUrls', 'metadataTags'],
    MarkdownDocumentReader: ['horizontalRuleCreateDocument', 'includeCodeBlock', 'includeBlockquote'],
    YamlDocumentReader: ['keysToUse', 'pointer']
};

const vectorTaskIcons = {
    AIVectorStore: '/images/database-plus.svg',
    AIVectorSearch: '/images/database-search.svg',
    AIVectorDelete: '/images/database-minus.svg'
};

const vectorStoreProviderLogos = {
    Pinecone: '/images/logos/pinecone.svg',
    Qdrant: '/images/logos/qdrant.svg',
    Weaviate: '/images/logos/weaviate.svg',
    SimWorkflow: '/images/logos/simworkflow.svg'
};

const vectorTaskImage = (task, resources) => {
    if (!Object.hasOwn(vectorTaskIcons, task?.type)) {
        return null;
    }
    const fallback = vectorTaskIcons[task.type];

    const resource = Object.entries(resources?.vectorStores || {}).find(([name]) =>
        task.vectorStore === `#/resources/vectorStores/${name.replaceAll('~', '~0').replaceAll('/', '~1')}`)?.[1];
    const provider = resource?.vectorStore?.provider;
    return Object.hasOwn(vectorStoreProviderLogos, provider) ? vectorStoreProviderLogos[provider] : fallback;
};

const taskFieldMatrix = {
    'Simple': {
        retryable: false
    },
    'Http': {
        retryable: true
    },
    'JQ': {
        retryable: true
    },
    'JsonConverter': {
        retryable: true
    },
    'Wait': {
        retryable: false
    },
    'Email': {
        retryable: true
    },
    'Map': {
        retryable: true
    },
    'Terminate': {
        retryable: false
    },
    'Decision': {
        retryable: false
    },
    'Parallel': {
        retryable: true
    },
    'AIChat': {
        retryable: true
    },
    'JsonDocumentReader': {
        retryable: true
    },
    'TextDocumentReader': {
        retryable: true
    },
    'HtmlDocumentReader': {
        retryable: true
    },
    'MarkdownDocumentReader': {
        retryable: true
    },
    'YamlDocumentReader': {
        retryable: true
    },
    'AIVectorStore': {
        retryable: true
    },
    'AIVectorSearch': {
        retryable: true
    },
    'AIVectorDelete': {
        retryable: true
    },
};

const taskTypeToStyle = {
    'JsonConverter': {
        style: 'json-converter',
        width: taskWidth,
        height: 40
    },
    'Simple': {
        style: 'task',
        width: taskWidth,
        height: 40
    },
    'Http': {
        style: 'http',
        width: taskWidth,
        height: 40
    },
    'JQ': {
        style: 'jq',
        width: taskWidth,
        height: 40
    },
    'Wait': {
        style: 'wait',
        width: taskWidth,
        height: 40
    },
    'Email': {
        style: 'email',
        width: taskWidth,
        height: 40
    },
    'Map': {
        style: 'map',
        width: taskWidth,
        height: 40
    },
    'Terminate': {
        style: 'end',
        width: 30,
        height: 30
    },
    'Start': {
        style: 'start',
        width: 30,
        height: 30
    },
    'AIChat': {
        style: 'ai-chat',
        width: taskWidth,
        height: 40
    },
    'JsonDocumentReader': {
        style: 'json-document-reader',
        width: taskWidth,
        height: 40
    },
    'TextDocumentReader': {
        style: 'text-document-reader',
        width: taskWidth,
        height: 40
    },
    'HtmlDocumentReader': {
        style: 'html-document-reader',
        width: taskWidth,
        height: 40
    },
    'MarkdownDocumentReader': {
        style: 'markdown-document-reader',
        width: taskWidth,
        height: 40
    },
    'YamlDocumentReader': {
        style: 'yaml-document-reader',
        width: taskWidth,
        height: 40
    },
    'AIVectorStore': {
        style: 'ai-vector-store',
        width: taskWidth,
        height: 40
    },
    'AIVectorSearch': {
        style: 'ai-vector-search',
        width: taskWidth,
        height: 40
    },
    'AIVectorDelete': {
        style: 'ai-vector-delete',
        width: taskWidth,
        height: 40
    },
}

const newWorkflow = {
    children: [
        {
            tasks: [],
            routes: [],
        },
    ],
};

const hasValue = value => typeof value !== 'undefined' && value !== null && value !== '';

const defaultRequiredChatModel = {
    credentials: '',
    provider: '',
    model: ''
};

const toAIChatModel = chatModel => ({
    ...defaultRequiredChatModel,
    ...(chatModel || {})
});

const toRequiredObject = value => typeof value === 'undefined' || value === null ? {} : value;

const toRetry = ({retryIntervalSeconds, retryMaxAttempts, retryBackoffRate} = {}) => {
    if (![retryIntervalSeconds, retryMaxAttempts, retryBackoffRate].some(hasValue)) {
        return null;
    }

    return {
        intervalSeconds: retryIntervalSeconds,
        maxAttempts: retryMaxAttempts,
        backoffRate: retryBackoffRate
    };
};

const toTaskRetry = retry => ({
    retryIntervalSeconds: retry?.intervalSeconds,
    retryMaxAttempts: retry?.maxAttempts,
    retryBackoffRate: retry?.backoffRate,
});

const terminalBranchTasks = (branchTasks, branchRoutes) => {
    const branchTaskIds = new Set(branchTasks.map(task => task.id));
    const branchRouteSourceTaskIds = new Set(
        branchRoutes
            .filter(route => branchTaskIds.has(route.source) && branchTaskIds.has(route.target))
            .map(route => route.source)
    );

    return branchTasks.filter(task => !branchRouteSourceTaskIds.has(task.id));
};

const isDefinitionPassThroughStartCell = (cell, model) => {
    const cellValue = cell?.getValue();
    if (!cell || !cell.isStart?.() || cellValue?.type ||
            !isEmpty(cellValue?.decisionTask?.name) ||
            !isEmpty(cellValue?.parallelTask?.name) ||
            cellValue?.splitType === 'And') {
        return false;
    }

    const outgoingRoutes = model.getEdges(cell, false, true, true);
    return outgoingRoutes.length === 1 && !outgoingRoutes[0].isConditional();
};

const toDefinitionStartCellId = (cellId, model, branchJoinTask) => {
    const cell = model.getCell(cellId);
    if (cellId !== branchJoinTask && isDefinitionPassThroughStartCell(cell, model)) {
        const outgoingRoutes = model.getEdges(cell, false, true, true);
        return outgoingRoutes[0].target.id;
    }

    return cellId;
};

const getWorkflowStartTaskCell = model => {
    const graphStartCellId = model.getRoot().children?.[0]?.getValue()?.startTask;
    const startCellId = toDefinitionStartCellId(graphStartCellId, model);
    return model.getCell(startCellId);
};

const hasPageVersion = pageVersion => pageVersion !== undefined && pageVersion !== null && pageVersion !== '';

const normalizePageReference = reference => {
    if (isEmpty(reference)) {
        return null;
    }

    if (typeof reference === 'string') {
        return {
            pageId: reference,
            pageVersion: null
        };
    }

    if (isEmpty(reference.pageId)) {
        return null;
    }

    return {
        pageId: reference.pageId,
        pageVersion: hasPageVersion(reference.pageVersion) ? reference.pageVersion : null
    };
};

const toPageReferencePayload = reference => {
    const normalized = normalizePageReference(reference);
    if (normalized === null) {
        return null;
    }

    return {
        pageId: normalized.pageId,
        pageVersion: hasPageVersion(normalized.pageVersion) ? Number(normalized.pageVersion) : null
    };
};

const getWorkflowStartPage = model => normalizePageReference(model.getRoot()?.getValue()?.workflowStartPage);

export const normalizeRouteGeometryPoints = points => {
    const routePoints = points || [];
    const point = routePoints.length === 1 ? routePoints[0] : null;
    const isZero = value => value != null && value !== '' && Number(value) === 0;

    return point != null && isZero(point.x) && isZero(point.y) ? [] : routePoints;
};

const setWorkflowStartPage = (model, workflowStartPage) => {
    const workflowCell = model.getRoot();
    model.setValue(workflowCell, {
        ...workflowCell.getValue(),
        workflowStartPage: normalizePageReference(workflowStartPage)
    });
};

const extractGeometry = (layer, layerIndex, model, tasks, routes) => {
    const childCount = layer.getChildCount();
    for (let i = 0; i < childCount; i++) {
        const cell = layer.getChildAt(i);
        if (cell.isVertex()) {
            const name = `${layerIndex}::${cell.getName()}`;
            const {x, y, width, height} = cell.geometry;
            tasks[name] = {
                x,
                y,
                width,
                height
            };
        } else if (cell.isEdge()) {
            if (isDefinitionPassThroughStartCell(cell.source, model)) {
                continue;
            }

            routes.push({
                name: cell.getName(),
                sourceTask: cell.source.getName(),
                targetTask: cell.target.getName(),
                geometryPoints: normalizeRouteGeometryPoints(cell.geometry?.points)
                    .map(point => ({x: point.x, y: point.y}))
            });
        }
    }
}

const toWorkflowDefinitionGeometry = model => {
    const workflowCell = model.getRoot();
    const tasks = {};
    const routes = [];

    const layerCount = model.getChildCount(workflowCell);
    for (let i = layerCount - 1; i >= 0; i--) {
        const layer = model.getChildAt(model.getRoot(), i);
        extractGeometry(layer, i, model, tasks, routes);
    }

    return {
        tasks,
        routes
    };
};

const processTasks = (cellId, tasks, model, branchJoinTask) => {
    const definitionStartCellId = toDefinitionStartCellId(cellId, model, branchJoinTask);
    if (definitionStartCellId !== cellId) {
        processTasks(definitionStartCellId, tasks, model, branchJoinTask);
        return;
    }

    const cell = model.getCell(cellId);
    if (!cell || cellId === branchJoinTask) {
        return;
    }

    const name = cell.getName();
    const cellValue = cell.getValue();
    const {
        description,
        type,
        splitType,
        joinTask,
        inputParameters,
        httpRequest,
        email,
        connectionTimeoutSeconds,
        readTimeoutSeconds,
        credentials,
        filter,
        itemsExpression,
        seconds,
        secondsExpression,
        timestamp,
        timestampExpression,
        terminationStatus,
        retryIntervalSeconds,
        retryMaxAttempts,
        retryBackoffRate,
        maxConcurrency,
        maxConcurrencyExpression,
        workflowOutput,
        userMessages,
        systemMessages,
        chatModel,
        mcpServers,
        memoryWindowLength,
        sourceType,
        sourceExpression,
        vectorStore,
        documentsExpression,
        queryExpression,
        idsExpression,
        topK,
        similarityThreshold,
        decisionTask,
        parallelTask
    } = cellValue;

    tasks[name] = {
        type,
        description,
        inputParameters,
        end: cellValue.end === false ? false : undefined
    };

    const retry = toRetry({retryIntervalSeconds, retryMaxAttempts, retryBackoffRate});

    if (type === 'Http') {
        tasks[name] = {
            ...tasks[name],
            httpRequest: toRequiredObject(httpRequest),
            connectionTimeoutSeconds,
            readTimeoutSeconds,
            credentials: isEmpty(credentials) ? null : credentials,
            retry
        }
    } else if (type === 'JQ') {
        tasks[name] = {
            ...tasks[name],
            filter,
            retry
        }
    } else if (type === 'Wait') {
        tasks[name] = {
            ...tasks[name],
            seconds,
            secondsExpression,
            timestamp,
            timestampExpression
        }
    } else if (type === 'Email') {
        tasks[name] = {
            ...tasks[name],
            email: toRequiredObject(email),
            retry
        }
    } else if (type === 'Terminate') {
        tasks[name] = {
            ...tasks[name],
            terminationStatus,
            workflowOutput
        }
    } else if (type === 'AIChat') {
        tasks[name] = {
            ...tasks[name],
            userMessages,
            systemMessages,
            chatModel: toAIChatModel(chatModel),
            mcpServers,
            ...(typeof memoryWindowLength !== 'undefined' ? {memoryWindowLength} : {}),
            retry
        }
    } else if (type === 'JsonConverter') {
        tasks[name] = {...tasks[name], sourceType, sourceExpression, retry};
    } else if (Object.hasOwn(documentReaderFields, type)) {
        tasks[name] = {
            ...tasks[name], sourceExpression,
            ...Object.fromEntries(documentReaderFields[type].filter(field => Object.hasOwn(cellValue, field))
                .map(field => [field, cellValue[field]])),
            retry
        };
    } else if (type === 'AIVectorStore') {
        tasks[name] = {
            ...tasks[name],
            vectorStore,
            documentsExpression,
            retry
        };
    } else if (type === 'AIVectorSearch') {
        tasks[name] = {
            ...tasks[name],
            vectorStore,
            queryExpression,
            topK,
            similarityThreshold,
            retry
        };
    } else if (type === 'AIVectorDelete') {
        tasks[name] = {
            ...tasks[name],
            vectorStore,
            idsExpression,
            retry
        };
    } else if (type === 'Map') {
        const itemProcessor = model.getRoot().children.find(layer => layer.id === cellValue.itemProcessorId);
        const itemProcessorStartTask = toDefinitionStartCellId(itemProcessor?.getValue()?.startTask, model);
        const itemProcessorStartCell = itemProcessorStartTask ? model.getCell(itemProcessorStartTask) : null;
        const itemProcessorTasks = {};

        if (itemProcessorStartCell) {
            processTasks(itemProcessorStartTask, itemProcessorTasks, model);
        }

        const mapStartTask = itemProcessorStartCell?.getName() || null;
        const mapTasks = isEmpty(itemProcessorTasks[mapStartTask]?.type) ? {} : itemProcessorTasks;

        tasks[name] = {
            ...tasks[name],
            itemsExpression,
            itemProcessor: {
                startTask: mapStartTask,
                tasks: mapTasks
            },
            maxConcurrency,
            maxConcurrencyExpression,
            retry
        }
    }

    let outgoingRoutes = model.getEdges(cell, false, true, true);

    if (splitType === 'And') {
        const branches = [];
        const nextTaskId = joinTask ? toDefinitionStartCellId(joinTask, model, branchJoinTask) : null;

        outgoingRoutes.forEach(outgoingRoute => {
            const branchTasks = {};
            processTasks(outgoingRoute.target.id, branchTasks, model, joinTask);

            if (!isEmpty(branchTasks)) {
                branches.push({
                    startTask: model.getCell(toDefinitionStartCellId(outgoingRoute.target.id, model, joinTask)).getName(),
                    tasks: branchTasks
                });
            }
        });

        let parallelTaskName = `${parallelTaskPrefix}${name}`;
        if (cell.isStart()) {
            parallelTaskName = name;
        } else {
            parallelTaskName = parallelTask?.name || parallelTaskName;
            tasks[name].nextTask = parallelTaskName;
        }

        const parallelRetry = cell.isStart() ? retry : toRetry(parallelTask);
        tasks[parallelTaskName] = {
            type: 'Parallel',
            description: cell.isStart() ? description : parallelTask?.description,
            inputParameters: cell.isStart() ? inputParameters : parallelTask?.inputParameters,
            branches,
            retry: parallelRetry,
            ...(nextTaskId && nextTaskId !== branchJoinTask ? {nextTask: model.getCell(nextTaskId).getName()} : {end: true})
        }

        if (nextTaskId && nextTaskId !== branchJoinTask) {
            processTasks(nextTaskId, tasks, model, branchJoinTask);
        }

        return;
    } else if (outgoingRoutes.length === 0) {
        tasks[name].end = true;
    } else if (outgoingRoutes.length === 1) {
        if (outgoingRoutes[0].target.id === branchJoinTask) {
            tasks[name].end = true;
            return;
        }

        const nextTask = model.getCell(outgoingRoutes[0].target.id).getName();
        if (outgoingRoutes[0].isConditional()) {
            const decisionTaskName = cell.isStart() ? name : decisionTask?.name || `${choiceTaskPrefix}${name}`;
            tasks[decisionTaskName] = {
                type: 'Decision',
                description: cell.isStart() ? description : decisionTask?.description,
                inputParameters: cell.isStart() ? inputParameters : decisionTask?.inputParameters,
                choices: [{
                    condition: outgoingRoutes[0].value?.condition,
                    nextTask,
                }]
            };

            if (!cell.isStart()) {
                tasks[name].nextTask = decisionTaskName;
            }
        } else {
            if (!isEmpty(decisionTask?.name)) {
                const decisionTaskName = cell.isStart() ? name : decisionTask.name;
                tasks[decisionTaskName] = {
                    type: 'Decision',
                    description: cell.isStart() ? description : decisionTask.description,
                    inputParameters: cell.isStart() ? inputParameters : decisionTask.inputParameters,
                    defaultTask: nextTask
                };

                if (!cell.isStart()) {
                    tasks[name].nextTask = decisionTaskName;
                }
            } else {
                tasks[name].nextTask = nextTask;
            }
        }
    } else {
        const choices = [];
        let defaultTask;
        outgoingRoutes.forEach(outgoingRoute => {
            const nextTask = model.getCell(outgoingRoute.target.id).getName();
            if (outgoingRoute.isConditional()) {
                choices.push({
                    condition: outgoingRoute.value?.condition,
                    nextTask
                });
            } else if (!defaultTask) {
                defaultTask = nextTask;
            }
        });

        let decisionTaskName = `${choiceTaskPrefix}${name}`;
        if (cell.isStart()) {
            decisionTaskName = name;
        } else {
            decisionTaskName = decisionTask?.name || decisionTaskName;
            tasks[name].nextTask = decisionTaskName;
        }

        tasks[decisionTaskName] = {
            type: 'Decision',
            description: cell.isStart() ? description : decisionTask?.description,
            inputParameters: cell.isStart() ? inputParameters : decisionTask?.inputParameters,
            defaultTask
        };

        if (!isEmpty(choices)) {
            tasks[decisionTaskName] = {
                ...tasks[decisionTaskName],
                choices
            };
        }
    }

    outgoingRoutes.forEach(outgoingRoute => {
        const nextTask = model.getCell(outgoingRoute.target.id).getName();
        if (!tasks.hasOwnProperty(nextTask) && outgoingRoute.target.id !== branchJoinTask) {
            processTasks(outgoingRoute.target.id, tasks, model, branchJoinTask);
        }
    });
}

const processTaskDefinitions = (name, taskDefinitions, children, childrenIndex, taskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames, previousTask, branchJoinTask) => {
    if (taskIdMap.has(name)) {
        return;
    }

    taskIdMap.set(name, uuid());

    const {tasks, routes} = children[childrenIndex];
    const geometryName = `${childrenIndex}::${name}`;

    const {
        description,
        inputParameters,
        httpRequest,
        email,
        connectionTimeoutSeconds,
        readTimeoutSeconds,
        credentials,
        seconds,
        secondsExpression,
        filter,
        itemsExpression,
        itemProcessor,
        timestamp,
        timestampExpression,
        type,
        terminationStatus,
        workflowOutput,
        nextTask,
        end,
        retry,
        maxConcurrency,
        maxConcurrencyExpression,
        choices,
        defaultTask,
        branches,
        userMessages,
        systemMessages,
        chatModel,
        mcpServers,
        memoryWindowLength,
        sourceType,
        sourceExpression,
        vectorStore,
        documentsExpression,
        queryExpression,
        idsExpression,
        topK,
        similarityThreshold
    } = taskDefinitions[name];
    const retryable = taskFieldMatrix[type]?.retryable === true;
    const controlPredecessor = ['Decision', 'Parallel'].includes(type) ? tasks.find(task =>
        task.name === previousTask && task.type && !task.decisionTask && !task.parallelTask &&
        Object.values(taskDefinitions).filter(definition => definition.nextTask === name ||
            definition.defaultTask === name || definition.choices?.some(choice => choice.nextTask === name)).length === 1
    ) : undefined;

    if (['Simple', 'Http', 'JQ', 'Email', 'Wait', 'Map', 'Terminate', 'AIChat', 'JsonConverter', ...Object.keys(documentReaderFields), 'AIVectorStore', 'AIVectorSearch', 'AIVectorDelete'].includes(type)) {
        const task = {
            id: taskIdMap.get(name),
            name,
            description,
            inputParameters,
            joinTask: null,
            joinType: 'Or',
            processor: normalizePageReference(taskProcessors?.[name]),
            splitType: 'Or',
            type,
            end,
            geometry: workflowDefinitionGeometry?.tasks?.[geometryName]
        }

        const taskRetry = retryable ? toTaskRetry(retry) : {};

        if (type === 'Http') {
            tasks.push({
                ...task,
                httpRequest,
                connectionTimeoutSeconds,
                readTimeoutSeconds,
                credentials,
                ...taskRetry
            });
        } else if (type === 'JQ') {
            tasks.push({
                ...task,
                filter,
                ...taskRetry
            });
        } else if (type === 'Email') {
            tasks.push({
                ...task,
                email,
                ...taskRetry
            });
        } else if (type === 'Wait') {
            tasks.push({
                ...task,
                seconds,
                secondsExpression,
                timestamp,
                timestampExpression
            });
        } else if (type === 'Terminate') {
            tasks.push({
                ...task,
                workflowOutput,
                terminationStatus
            });
        } else if (type === 'AIChat') {
            tasks.push({
                ...task,
                userMessages,
                systemMessages,
                chatModel: toAIChatModel(chatModel),
                mcpServers,
                ...(typeof memoryWindowLength !== 'undefined' ? {memoryWindowLength} : {}),
                ...taskRetry
            });
        } else if (type === 'JsonConverter') {
            tasks.push({...task, sourceType, sourceExpression, ...taskRetry});
        } else if (Object.hasOwn(documentReaderFields, type)) {
            tasks.push({...task, sourceExpression,
                ...Object.fromEntries(documentReaderFields[type].filter(field => Object.hasOwn(taskDefinitions[name], field))
                    .map(field => [field, taskDefinitions[name][field]])),
                ...taskRetry});
        } else if (['AIVectorStore', 'AIVectorSearch', 'AIVectorDelete'].includes(type)) {
            tasks.push({
                ...task,
                vectorStore,
                documentsExpression,
                queryExpression,
                idsExpression,
                topK,
                similarityThreshold,
                ...taskRetry
            });
        } else if (type === 'Map') {
            const itemProcessorId = uuid();
            tasks.push({
                ...task,
                itemsExpression,
                itemProcessorId,
                maxConcurrency,
                maxConcurrencyExpression,
                ...taskRetry
            });

            children.push({
                id: itemProcessorId,
                tasks: [],
                routes: []
            });

            const itemProcessorIndex = children.length - 1;
            if (itemProcessor?.startTask) {
                const itemTaskIdMap = new Map();
                processTaskDefinitions(itemProcessor.startTask, itemProcessor.tasks || {}, children, itemProcessorIndex, itemTaskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames);
                children[itemProcessorIndex].startTask = itemTaskIdMap.get(itemProcessor.startTask);
            }
        } else {
            tasks.push(task);
        }

        if (nextTask) {
            processTaskDefinitions(nextTask, taskDefinitions, children, childrenIndex, taskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames, name, branchJoinTask);

            if (taskIdMap.has(nextTask)) {
                const route = workflowDefinitionGeometry?.routes?.find(route => route.sourceTask === name && route.targetTask === nextTask);
                routes.push({
                    id: uuid(),
                    name: route?.name,
                    source: taskIdMap.get(name),
                    target: taskIdMap.get(nextTask),
                    routingType: 'UnConditional',
                    geometry: {
                        points: route?.geometryPoints
                    }
                });
            }
        }
    } else if (type === 'Decision') {
        let lastTask = controlPredecessor;
        const decisionTask = {
            name,
            description,
            inputParameters
        };

        if (lastTask) {
            lastTask.decisionTask = decisionTask;
            taskIdMap.delete(name);
        } else {
            lastTask = {
                id: taskIdMap.get(name),
                name,
                description,
                inputParameters,
                decisionTask,
                processor: normalizePageReference(taskProcessors?.[name]),
                splitType: 'Or',
                style: 'start',
                geometry: workflowDefinitionGeometry?.tasks?.[geometryName]
            };

            tasks.push(lastTask);
        }

        if (defaultTask) {
            processTaskDefinitions(defaultTask, taskDefinitions, children, childrenIndex, taskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames, undefined, branchJoinTask);

            if (taskIdMap.has(defaultTask)) {
                const route = workflowDefinitionGeometry?.routes?.find(route => route.sourceTask === lastTask?.name && route.targetTask === defaultTask);
                routes.push({
                    id: uuid(),
                    name: route?.name,
                    source: taskIdMap.get(lastTask?.name),
                    target: taskIdMap.get(defaultTask),
                    routingType: 'UnConditional',
                    geometry: {
                        points: route?.geometryPoints
                    }
                });
            }
        }

        (choices || []).forEach(choice => {
            processTaskDefinitions(choice.nextTask, taskDefinitions, children, childrenIndex, taskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames, undefined, branchJoinTask);

            if (taskIdMap.has(choice.nextTask)) {
                const route = workflowDefinitionGeometry?.routes?.find(route => route.sourceTask === lastTask?.name && route.targetTask === choice.nextTask);
                routes.push({
                    id: uuid(),
                    name: route?.name,
                    condition: choice.condition,
                    source: taskIdMap.get(lastTask?.name),
                    target: taskIdMap.get(choice.nextTask),
                    routingType: 'Conditional',
                    geometry: {
                        points: route?.geometryPoints
                    }
                });
            }
        });
    } else if (type === 'Parallel') {
        let lastTask = controlPredecessor;
        const parallelTask = {
            name,
            description,
            inputParameters,
            ...toTaskRetry(retry)
        };

        if (lastTask) {
            lastTask.splitType = 'And';
            lastTask.parallelTask = parallelTask;
            taskIdMap.delete(name);
        } else {
            lastTask = {
                id: taskIdMap.get(name),
                name,
                description,
                inputParameters,
                ...toTaskRetry(retry),
                parallelTask,
                processor: normalizePageReference(taskProcessors?.[name]),
                splitType: 'And',
                style: 'start',
                geometry: workflowDefinitionGeometry?.tasks?.[geometryName]
            };

            tasks.push(lastTask);
        }

        let joinTask;
        if (nextTask) {
            processTaskDefinitions(nextTask, taskDefinitions, children, childrenIndex, taskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames, undefined, branchJoinTask);

            joinTask = tasks.find(t => t.name === nextTask);
            joinTask.joinType = 'And';

        } else if (end === true) {
            const joinLabel = ` - ${mxResources.get('join')}`;
            let joinName = `${name.slice(0, taskNameMaxLength - joinLabel.length).replace(/[\uD800-\uDBFF]$/, '')}${joinLabel}`;
            let suffix = 1;
            while (taskNames.has(joinName)) {
                const numberedLabel = `${joinLabel} ${++suffix}`;
                joinName = `${name.slice(0, taskNameMaxLength - numberedLabel.length).replace(/[\uD800-\uDBFF]$/, '')}${numberedLabel}`;
            }
            taskNames.add(joinName);
            joinTask = {
                id: uuid(),
                name: joinName,
                style: taskTypeToStyle.Terminate.style,
                type: 'Terminate',
                terminationStatus: 'Completed',
                splitType: 'Or',
                joinType: 'And',
                geometry: workflowDefinitionGeometry?.tasks?.[`${childrenIndex}::${joinName}`]
            };
            tasks.push(joinTask);
        }
        lastTask.joinTask = joinTask?.id;

        const branchCellIds = [];
        (branches || []).forEach(branch => {
            const branchTaskStartIndex = tasks.length;
            const branchRouteStartIndex = routes.length;
            const branchTaskIdMap = new Map();
            processTaskDefinitions(branch.startTask, branch.tasks, children, childrenIndex, branchTaskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames, undefined, joinTask || branchJoinTask);
            const branchTasks = tasks.slice(branchTaskStartIndex);
            branchCellIds.push(...branchTasks.map(task => task.id));
            const branchRoutes = routes.slice(branchRouteStartIndex);

            if (branchTaskIdMap.has(branch.startTask)) {
                const route = workflowDefinitionGeometry?.routes?.find(route => route.sourceTask === lastTask?.name && route.targetTask === branch.startTask);
                routes.push({
                    id: uuid(),
                    name: route?.name,
                    source: taskIdMap.get(lastTask?.name),
                    target: branchTaskIdMap.get(branch.startTask),
                    routingType: 'UnConditional',
                    geometry: {
                        points: route?.geometryPoints
                    }
                });
            }

            terminalBranchTasks(branchTasks, branchRoutes).forEach(branchTask => {
                if (!joinTask) {
                    return;
                }

                const route = workflowDefinitionGeometry?.routes?.find(route => route.sourceTask === branchTask.name && route.targetTask === joinTask.name);
                routes.push({
                    id: uuid(),
                    name: route?.name,
                    source: branchTask.id,
                    target: joinTask.id,
                    routingType: 'UnConditional',
                    geometry: {
                        points: route?.geometryPoints
                    }
                });
            });
        });
        if (!nextTask && end === true && joinTask && !joinTask.geometry) {
            const layer = children[childrenIndex];
            layer.generatedTerminalJoins ??= [];
            layer.generatedTerminalJoins.push({joinId: joinTask.id, branchCellIds});
        }
    }
}

const fromWorkflowDefinition = workflowDefinitionWithGeometry => {
    const {workflowDefinition, workflowDefinitionGeometry, taskProcessors = {}, workflowStartPage} = workflowDefinitionWithGeometry;

    const {
        name,
        description,
        startTask,
        timeoutSeconds,
        failureWorkflow,
        failureWorkflowVersion,
        maxConcurrency,
        resources
    } = workflowDefinition;
    const children = [
        {
            tasks: [],
            routes: [],
        },
    ];
    const taskIdMap = new Map();
    const taskNames = new Set();
    const collectTaskNames = tasks => Object.entries(tasks || {}).forEach(([taskName, task]) => {
        taskNames.add(taskName);
        collectTaskNames(task.itemProcessor?.tasks);
        task.branches?.forEach(branch => collectTaskNames(branch.tasks));
    });
    collectTaskNames(workflowDefinition.tasks);

    processTaskDefinitions(startTask, workflowDefinition.tasks, children, 0, taskIdMap, workflowDefinitionGeometry, taskProcessors, taskNames);

    if (Object.hasOwn(workflowDefinitionWithGeometry, 'taskProcessorPages')) {
        children.forEach(child => child.tasks.forEach(task => {
            if (task.type === 'Simple') {
                task.templateSourceTaskName = task.name;
            }
        }));
    }

    children[0].startTask = taskIdMap.get(startTask);

    return {
        name,
        description,
        timeoutSeconds,
        failureWorkflow,
        failureWorkflowVersion,
        maxConcurrency,
        resources,
        workflowStartPage: normalizePageReference(workflowStartPage),
        children,
        geometry: workflowDefinitionGeometry
    };
}

const toWorkflowDefinition = model => {
    const {
        name,
        description,
        timeoutSeconds,
        failureWorkflow,
        failureWorkflowVersion,
        maxConcurrency,
        resources
    } = model.getRoot().getValue();
    const {startTask: graphStartCellId} = model.getRoot().children[0].getValue();
    const startCellId = toDefinitionStartCellId(graphStartCellId, model);
    let tasks = {};

    processTasks(startCellId, tasks, model);

    if (Object.keys(tasks).length === 1 && !Object.values(tasks)[0].type) {
        tasks = {};
    }

    return {
        name,
        description,
        startTask: model.getCell(startCellId).getName(),
        timeoutSeconds,
        failureWorkflow: isEmpty(failureWorkflow) ? null : failureWorkflow,
        failureWorkflowVersion: isEmpty(failureWorkflowVersion) ? null : failureWorkflowVersion,
        maxConcurrency,
        resources,
        tasks
    }
}

const toTaskProcessors = model => {
    const workflowCell = model.getRoot();
    const toTaskProcessors = {};

    (function convertCells(cell) {
        if (cell.isVertex()) {
            const value = cell.getValue();
            const processor = toPageReferencePayload(value.processor);
            if (!isEmpty(processor)) {
                toTaskProcessors[cell.getName()] = processor;
            }
        }

        const childCount = cell.getChildCount();
        for (let i = 0; i < childCount; i++) {
            convertCells(cell.getChildAt(i));
        }
    })(workflowCell);

    return toTaskProcessors;
};

const toWorkflowStartPage = model => {
    const workflowStartPage = getWorkflowStartPage(model);
    return toPageReferencePayload(workflowStartPage);
};

const toTemplateTaskNames = model => {
    const taskNames = [];

    (function convertCells(cell) {
        if (cell.isVertex()) {
            const value = cell.getValue();
            if (value.type === 'Simple' && typeof value.templateSourceTaskName === 'string') {
                taskNames.push([cell.getName(), value.templateSourceTaskName]);
            }
        }

        const childCount = cell.getChildCount();
        for (let i = 0; i < childCount; i++) {
            convertCells(cell.getChildAt(i));
        }
    })(model.getRoot());

    return Object.fromEntries(taskNames);
};

export {
    toWorkflowDefinitionGeometry,
    toWorkflowDefinition,
    fromWorkflowDefinition,
    toTaskProcessors,
    toTemplateTaskNames,
    toWorkflowStartPage,
    getWorkflowStartTaskCell,
    getWorkflowStartPage,
    setWorkflowStartPage,
    normalizePageReference,
    taskTypeToStyle,
    vectorTaskImage,
    newWorkflow,
    choiceTaskPrefix,
    parallelTaskPrefix
};
